import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { goToInterestForm, utmParams } from "../../utilities/screeningApi";
import { useDualMsal } from "../../contexts/MsalContext";
import { confirmSignOut } from "../../utilities/screeningImportStore";

const navLinks = [
  { href: "#eligibility", label: "Eligibility" },
  { href: "#how-it-works", label: "How it works" },
  { href: "#science", label: "The science" },
  { href: "#faq", label: "FAQs" },
  { href: "#team", label: "Team" },
  { href: "#contact", label: "Contact" },
];

export default function PublicHeader({ onLoginClick, onInterestClick }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { currentUser, isAuthenticated, logout } = useDualMsal();
  const navigate = useNavigate();
  // Pages other than Home don't pass a handler; without this fallback the
  // button did nothing on /invite and /book. Wrapped so that the click
  // event is never taken for the utm_ values.
  const handleInterest = onInterestClick || (() => goToInterestForm(utmParams(window.location.search)));
  const location = useLocation();

  const isHomePage = location.pathname === "/" || location.pathname === "";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMenu = () => setIsMenuOpen(false);

  const getDashboardUrl = () => {
    if (!currentUser) return "/";
    const roles = currentUser.roles || [];
    if (roles.includes("PRADAM.Admin") || roles.includes("PRADAM.PI")) return "/admin";
    if (roles.includes("PRADAM.Ppt")) return "/participant";
    return "/";
  };

  const handleUserClick = () => {
    navigate(getDashboardUrl());
    closeMenu();
  };

  const handleLogout = () => {
    // A screening import's one-time links are lost on signing out: ask first.
    if (!confirmSignOut()) return;
    logout();
    closeMenu();
  };

  return (
    <header
      // transition-all + backdrop-blur-md was murder on iOS Safari
      // (Chrome iOS uses WebKit too): the blur forces a full-screen
      // composite layer every scroll frame and transition-all kept
      // observing every animatable prop. Both delayed the hamburger
      // and menu paint badly on real iPhones. Solid white + opt-in blur
      // on ≥md viewports keeps the desktop aesthetic without the cost.
      className={`font-body fixed top-0 left-0 right-0 z-50 transition-[background-color,padding-top,padding-bottom,box-shadow] duration-300 ${
        isScrolled
          ? "bg-white md:bg-white/95 md:backdrop-blur-md shadow-md py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="container">
        <div className="flex items-center justify-between relative">
          {/* Brand */}
          <Link
            to="/"
            className="flex items-start flex-1 min-[1100px]:flex-none mr-4"
            aria-label="PRADAM Study Home"
          >
            <img src="/logo.png" alt="" className="w-[52px] h-[52px] object-contain -mr-0.5" aria-hidden="true" />
            <div className="flex items-baseline gap-2 mt-[1.8px] -ml-[2.5px]">
              <span className="text-[37px] leading-none font-bold font-heading text-text-main tracking-tight">
                RADAM
              </span>
              <span className="text-2xl font-medium text-text-muted">
                Study
              </span>
            </div>
          </Link>

          {/* Navigation — home page only */}
          {isHomePage && (
            <nav
              // Only the props we actually animate (transform, opacity,
              // visibility). transition-all + will-change=auto was making
              // WebKit re-observe every property on each menu tap.
              className={`
                absolute min-[1100px]:static top-full left-0 right-0
                min-[1100px]:flex min-[1100px]:items-center min-[1100px]:gap-8
                bg-white min-[1100px]:bg-transparent
                shadow-lg min-[1100px]:shadow-none
                transition-[transform,opacity,visibility] duration-300
                will-change-[transform,opacity]
                ${
                  isMenuOpen
                    ? "translate-y-0 opacity-100 visible"
                    : "-translate-y-4 opacity-0 invisible min-[1100px]:visible min-[1100px]:translate-y-0 min-[1100px]:opacity-100"
                }
              `}
              aria-label="Main navigation"
            >
              <ul className="flex flex-col sm:flex-row sm:flex-wrap min-[1100px]:flex-nowrap min-[1100px]:items-center gap-4 min-[1100px]:gap-6 p-6 min-[1100px]:p-0">
                {navLinks.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className="block text-text-main hover:text-primary transition-colors font-medium text-lg min-[1100px]:text-base"
                      onClick={closeMenu}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}

                {/* Mobile-only actions */}
                <li className="sm:hidden pt-4 border-t border-border/20 flex flex-col gap-4">
                  {isAuthenticated && currentUser ? (
                    <button
                      onClick={handleUserClick}
                      className="btn btn-accent w-full justify-center px-6 py-3 text-sm"
                    >
                      {currentUser.accountType === "B2C" ? "My PRADAM" : "Admin Panel"}
                    </button>
                  ) : (
                    <button
                      onClick={() => { handleInterest(); closeMenu(); }}
                      className="btn btn-accent w-full justify-center px-6 py-3 text-sm"
                    >
                      Express Interest
                    </button>
                  )}
                  {isAuthenticated ? (
                    <button
                      onClick={handleLogout}
                      className="btn btn-subtle w-full justify-center px-6 py-3 text-sm border border-text-main/10"
                    >
                      Logout
                    </button>
                  ) : (
                    <button
                      onClick={() => { onLoginClick?.(); closeMenu(); }}
                      className="btn btn-subtle w-full justify-center px-6 py-3 text-sm border border-text-main/10"
                    >
                      Login
                    </button>
                  )}
                </li>
              </ul>
            </nav>
          )}

          {/* Desktop auth buttons */}
          <div className="flex items-center gap-4">
            <div className="hidden sm:block">
              {isAuthenticated && currentUser ? (
                <button
                  onClick={handleUserClick}
                  className="btn btn-accent px-6 py-3 text-sm"
                >
                  {currentUser.accountType === "B2C" ? "My PRADAM" : "Admin Panel"}
                </button>
              ) : (
                <button
                  onClick={handleInterest}
                  className="btn btn-accent px-6 py-3 text-sm"
                >
                  Express Interest
                </button>
              )}
            </div>

            <div className="hidden sm:block">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="btn btn-subtle px-6 py-3 text-sm border border-text-main/10"
                >
                  Logout
                </button>
              ) : (
                <button
                  onClick={onLoginClick}
                  className="btn btn-subtle px-6 py-3 text-sm border border-text-main/10"
                >
                  Login
                </button>
              )}
            </div>
          </div>

          {/* Hamburger — home page only */}
          {isHomePage && (
            <button
              className="min-[1100px]:hidden flex flex-col gap-1.5 w-8 h-8 justify-center items-center ml-2"
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span className={`block w-6 h-0.5 bg-text-main transition-[transform,opacity] will-change-transform ${isMenuOpen ? "rotate-45 translate-y-2" : ""}`} />
              <span className={`block w-6 h-0.5 bg-text-main transition-[transform,opacity] will-change-[opacity] ${isMenuOpen ? "opacity-0" : ""}`} />
              <span className={`block w-6 h-0.5 bg-text-main transition-[transform,opacity] will-change-transform ${isMenuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}